import React, { useEffect, useState } from "react";
import ChatPanel from "./ChatPanel";
import { useAuth, getGoogleLoginUrl } from "../AuthContext";
import { listConversations, messageStart } from "../api";

/**
 * Floating chat button for authenticated visitors.
 * Also exposes a global helper `window.6amico_startChat({ target_kind, target_id, prefill })`
 * so any page (Comune detail, Product card, Event tile) can launch the chat.
 */
export default function ChatLauncher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [autoOpenConvId, setAutoOpenConvId] = useState(null);
  const [starter, setStarter] = useState(null); // { target_kind, target_id, target_label, prefill }
  const [pendingText, setPendingText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  async function loadUnread() {
    try {
      const r = await listConversations();
      const total = (r.conversations || []).reduce((s, c) => s + (c.visitor_unread || 0), 0);
      window.dispatchEvent(new CustomEvent("6amico:messages-unread", { detail: total }));
    } catch {}
  }

  useEffect(() => {
    if (!user) { window.dispatchEvent(new CustomEvent("6amico:messages-unread", { detail: 0 })); return; }
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Expose global API for "Contatta" buttons across pages
  useEffect(() => {
    window.amicoStartChat = (config) => {
      if (!user) {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        window.location.href = getGoogleLoginUrl(window.location.pathname);
        return;
      }
      setStarter(config);
      setPendingText(config.prefill || "");
      setOpen(true);
      setAutoOpenConvId(null);
      setError("");
    };
    return () => { delete window.amicoStartChat; };
  }, [user]);

  // Il pannello si apre dall'icona messaggi in header (evento globale)
  useEffect(() => {
    const onOpen = () => { setStarter(null); setAutoOpenConvId(null); setOpen(true); };
    window.addEventListener("6amico:open-messages", onOpen);
    return () => window.removeEventListener("6amico:open-messages", onOpen);
  }, []);

  async function startConversation(e) {
    e.preventDefault();
    if (!starter || !pendingText.trim()) return;
    setSending(true); setError("");
    try {
      const r = await messageStart({
        target_kind: starter.target_kind,
        target_id: starter.target_id,
        text: pendingText.trim(),
      });
      setAutoOpenConvId(r.conversation.id);
      setStarter(null); setPendingText("");
      loadUnread();
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore nell'avvio della chat");
    } finally { setSending(false); }
  }

  if (!user) return null;

  return (
    <>


      {open && (
        <div className="fixed inset-0 z-50 md:inset-auto md:top-16 md:right-5 md:w-[640px] md:max-w-[calc(100vw-2.5rem)] md:rounded-2xl md:overflow-hidden bg-white md:shadow-2xl md:border md:border-borderline" data-testid="chat-overlay">
          {starter ? (
            <div className="h-full flex flex-col" data-testid="chat-starter">
              <div className="px-4 py-3 border-b border-borderline flex items-center justify-between">
                <h3 className="font-display text-lg text-ink">Contatta {starter.target_label}</h3>
                <button onClick={() => { setStarter(null); setOpen(false); }} className="text-ink2 hover:text-terracotta text-xs">Annulla</button>
              </div>
              <form onSubmit={startConversation} className="flex-1 p-4 flex flex-col">
                <p className="text-sm text-ink2 mb-2">Scrivi un messaggio diretto. Riceverà una notifica e ti risponderà appena possibile.</p>
                <textarea
                  data-testid="chat-starter-text"
                  value={pendingText} onChange={e => setPendingText(e.target.value)}
                  placeholder="Es. Buongiorno, vorrei avere informazioni..."
                  rows={5} maxLength={2000} required
                  className="w-full text-sm border border-borderline rounded-lg p-3 bg-parchment focus:border-terracotta outline-none flex-1"
                />
                {error && <p className="text-terracotta text-sm mt-2">{error}</p>}
                <button type="submit" disabled={sending || !pendingText.trim()}
                  data-testid="chat-starter-send"
                  className="mt-3 bg-terracotta hover:bg-ink text-parchment rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50">
                  {sending ? "Invio..." : "Invia messaggio"}
                </button>
              </form>
            </div>
          ) : (
            <ChatPanel role="visitor" onClose={() => setOpen(false)} autoOpenConvId={autoOpenConvId}/>
          )}
        </div>
      )}
    </>
  );
}

